import React, { useState, useRef, useEffect } from 'react';
import { Download, Eye, X, Users } from 'lucide-react';
import axios from 'axios';

const Employee360FamilyPensionCard = ({ employeeId, profile, loading }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [familyPensionReport, setFamilyPensionReport] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    const fetchFamilyPensionReport = async () => {
      try {
        const res = await axios.get(`/v1/employee-master/${employeeId}/family-pension-report`).catch(err => {
          console.error("No family pension report or data setup", err);
          return { data: null };
        });
        setFamilyPensionReport(res.data);
      } catch (error) {
        console.error("Failed to fetch family pension report", error);
      }
    };
    fetchFamilyPensionReport();
  }, [employeeId]);

  const loadHtml2Pdf = async () => {
    if (!window.html2pdf) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }
  };

  const handleDownloadPDF = async () => {
    const printContent = printRef.current;
    if (!printContent) return;

    await loadHtml2Pdf();

    // Create an isolated container specifically for the Family Pension PDF.
    // This prevents modal, flex, overflow, and preview styles from
    // affecting the downloaded PDF.
    const pdfContainer = document.createElement('div');

    pdfContainer.style.position = 'absolute';
    pdfContainer.style.left = '-99999px';
    pdfContainer.style.top = '0';
    pdfContainer.style.width = '794px';
    pdfContainer.style.margin = '0';
    pdfContainer.style.padding = '0';
    pdfContainer.style.background = '#ffffff';
    pdfContainer.style.boxSizing = 'border-box';

    const pdfContent = printContent.cloneNode(true);

    // Reset layout-related styles for PDF rendering.
    pdfContent.style.position = 'static';
    pdfContent.style.margin = '0';
    pdfContent.style.paddingTop = '0';
    pdfContent.style.paddingBottom = '0';
    pdfContent.style.paddingLeft = '0';
    pdfContent.style.paddingRight = '0';
    pdfContent.style.width = '100%';
    pdfContent.style.maxWidth = 'none';
    pdfContent.style.minHeight = '0';
    pdfContent.style.height = 'auto';
    pdfContent.style.background = '#ffffff';

    pdfContainer.appendChild(pdfContent);
    document.body.appendChild(pdfContainer);

    // Allow the browser to render the cloned content before capturing it.
    await new Promise(resolve => requestAnimationFrame(resolve));

    const opt = {
      // Small controlled A4 margin.
      margin: [5, 10, 10, 10],

      filename: `Family_Pension_Calculation_${employeeId}.pdf`,

      image: {
        type: 'jpeg',
        quality: 0.98
      },

      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0
      },

      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      },

      pagebreak: {
        mode: ['css', 'legacy']
      }
    };

    try {
      await window
        .html2pdf()
        .set(opt)
        .from(pdfContent)
        .save();
    } finally {
      // Remove temporary PDF-only DOM.
      document.body.removeChild(pdfContainer);
    }
  };

  if (loading) return null;

  const fpr = familyPensionReport;

  // Use backend data with fallbacks
  const name = fpr?.employee_name || (profile?.personal?.full_name || `${profile?.personal?.first_name || ''} ${profile?.personal?.last_name || ''}`.trim() || 'N/A');
  const designation = fpr?.designation || profile?.employment?.designation || 'N/A';

  const joinDate = fpr?.date_of_appointment ? new Date(fpr.date_of_appointment) : (profile?.employment?.join_date ? new Date(profile.employment.join_date) : null);
  const field5Date = fpr?.field_5_date ? new Date(fpr.field_5_date) : null;
  const dobDate = fpr?.date_of_birth ? new Date(fpr.date_of_birth) : (profile?.personal?.date_of_birth ? new Date(profile.personal.date_of_birth) : null);

  const formatDateDot = (d) => {
    if (!d || isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${d.getFullYear()}`;
  };

  const attainmentDate = fpr?.attainment_date ? new Date(fpr.attainment_date) : null;
  const familyPensionFromDate = fpr?.normal_family_pension_start_date ? new Date(fpr.normal_family_pension_start_date) : null;

  const basicPayRaw = fpr?.pay || profile?.employment?.basic_pay || profile?.payroll?.basic_pay || profile?.basic_pay || 76400;

  const thirtyPercent = fpr?.rule_54_2?.thirty_percent_of_pay || Math.round(basicPayRaw * 0.3);
  const thirtyPercentPension = fpr?.rule_54_2?.family_pension || Math.max(9000, thirtyPercent);

  const fiftyPercent = fpr?.rule_54_2?.fifty_percent_of_pay || Math.round(basicPayRaw * 0.5);
  const twiceFamily = fpr?.rule_54_2?.twice_family_pension || (thirtyPercentPension * 2);
  const retirementPension = fpr?.retirement_pension || Math.round(basicPayRaw / 2);
  const leastOfFamily = fpr?.rule_54_2?.least_amount || Math.min(fiftyPercent, twiceFamily, retirementPension);

  const enhancedPeriodYears = fpr?.enhanced_period_years || 7;
  const field5Label = fpr?.field_5_label || 'Date of retirement';
  const attainmentLabel = fpr?.attainment_label || 'Date of attainment of sixty seven years';

  const formatCurrencyInt = (val) => Number(val).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-green-50 text-green-600 rounded-lg">
            <Users size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Family Pension Calculation</h3>
            <p className="text-sm text-gray-500">View and download the family pension calculation report.</p>
          </div>
        </div>
        <button
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-100 font-medium transition-colors"
        >
          <Eye size={18} />
          Preview
        </button>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-gray-50 rounded-xl shadow-2xl w-[1000px] max-w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-800">Family Pension Calculation Preview</h2>
              <div className="flex gap-3 items-center">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="bg-white rounded-lg border border-gray-200 p-12 shadow-sm overflow-x-auto">
                <div ref={printRef} className="max-w-4xl mx-auto w-full text-black bg-white">
                  <div className="text-center mb-8">
                    <div className="font-bold text-[18px] mb-1 tracking-wide uppercase">FAMILY PENSION CALCULATION</div>
                  </div>
                  <div className="space-y-2 text-[15px] font-serif px-12">

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1">1</div>
                      <div className="col-span-5">Name</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5 font-bold uppercase">{name}</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1">2</div>
                      <div className="col-span-5">Designation</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5 uppercase">{designation}</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1">3</div>
                      <div className="col-span-5">Pay</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">{formatCurrencyInt(basicPayRaw)}/-</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1">4</div>
                      <div className="col-span-5">Date of Appointment</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">{formatDateDot(joinDate)}</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1">5</div>
                      <div className="col-span-5 font-bold">{field5Label}</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">{formatDateDot(field5Date)}</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1">6</div>
                      <div className="col-span-5">Date of Birth</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">{formatDateDot(dobDate)}</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1">7</div>
                      <div className="col-span-5">{attainmentLabel}</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">{formatDateDot(attainmentDate)}</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1">8</div>
                      <div className="col-span-5">Retirement Pension</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">Rs. {formatCurrencyInt(retirementPension)}/-</div>
                    </div>

                    {/* Rules section */}
                    <div className="mt-8 mb-2 font-bold">Rule 54(2)</div>
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1"></div>
                      <div className="col-span-1">i)</div>
                      <div className="col-span-4">30 percent of pay i.e. = {formatCurrencyInt(basicPayRaw)}/-</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">{formatCurrencyInt(thirtyPercent)}/-</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-2"></div>
                      <div className="col-span-4">Minimum 9000/- Max.<br />Hence Family Pension is Rs.</div>
                      <div className="col-span-1 text-center"><br />:</div>
                      <div className="col-span-5"><br />{formatCurrencyInt(thirtyPercentPension)}/-</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start mt-2">
                      <div className="col-span-1"></div>
                      <div className="col-span-1">ii)</div>
                      <div className="col-span-4">50 percent of pay</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">{formatCurrencyInt(fiftyPercent)}/-</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1"></div>
                      <div className="col-span-1">iii)</div>
                      <div className="col-span-4">Twice Family pension</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">Rs.{formatCurrencyInt(twiceFamily)}/-</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1"></div>
                      <div className="col-span-1">iv)</div>
                      <div className="col-span-4">Retirement Pension</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">Rs. {formatCurrencyInt(retirementPension)}/-</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-1"></div>
                      <div className="col-span-1">v)</div>
                      <div className="col-span-4">Least of (ii) & (iii) (iv)</div>
                      <div className="col-span-1 text-center">:</div>
                      <div className="col-span-5">Rs.{formatCurrencyInt(leastOfFamily)}/-</div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 items-start mt-2">
                      <div className="col-span-1"></div>
                      <div className="col-span-1">vi)</div>
                      <div className="col-span-10 text-justify">
                        Shall not exceed retirement pension at (iv) above and, shall not be less than at (i) above i.e. the Family pension.
                      </div>
                    </div>

                    {/* Final Declaration */}
                    <div className="mt-12">
                      Therefore Family pension for<br />
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-5">({enhancedPeriodYears}) years i.e. upto</div>
                        <div className="col-span-1 text-center">:</div>
                        <div className="col-span-6 font-bold">{formatDateDot(attainmentDate)}</div>
                      </div>
                      shall be <span className="font-bold">Rs. {formatCurrencyInt(leastOfFamily)}/-</span> P.m. plus relief.
                    </div>

                    <div className="mt-8">
                      <span className="font-bold">Family Pension from {formatDateDot(familyPensionFromDate)}<br />
                        shall be Rs. {formatCurrencyInt(thirtyPercentPension)}/- Plus relief, if any</span>
                    </div>

                  </div>

                  {/* Signatures */}
                  <div className="mt-20 px-4">
                    <div>CHIEF ACCOUNTANT</div>
                  </div>

                  <div className="mt-16 mb-8 flex justify-end px-4">
                    <div>CHIEF EXECUTIVE OFFICER,PUNE</div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Employee360FamilyPensionCard;
